import { useState } from "react";
import { motion } from "framer-motion";
import { differenceInDays, differenceInHours } from "date-fns";
import { RelatedMarketSuggestions } from "./RelatedMarketSuggestions";
import { TOKEN_RAIN, TOKEN_USDT } from "../../utils/constants";
import { getTokenSymbol, getBackgroundColor, formatNumbers } from "../../utils/helpers";
import { useTheme } from "../../contexts/ThemeProvider";
import SocialShareModal from "./SocialShareModal";
import { ROUTES } from "../../constants/Routes";

const Raindrop = ({ delay, x, duration, size, color }) => (
  <motion.div
    className="gm-success-raindrop"
    style={{ left: `${x}%`, top: -30 }}
    initial={{ y: -30, opacity: 0 }}
    animate={{
      y: ["0vh", "105vh"],
      opacity: [0, 0.9, 0.9, 0],
    }}
    transition={{
      duration,
      delay,
      repeat: Infinity,
      ease: "linear",
    }}
  >
    <div
      style={{
        width: size,
        height: size * 3,
        borderRadius: "50% 50% 50% 50% / 60% 60% 40% 40%",
        background: color,
        filter: `blur(0.3px) drop-shadow(0 0 ${size}px ${color})`,
      }}
    />
  </motion.div>
);

const Splash = ({ delay, x, color }) => (
  <motion.div
    className="gm-success-splash"
    style={{ left: `${x}%` }}
    initial={{ scale: 0, opacity: 0 }}
    animate={{
      scale: [0, 2, 3],
      opacity: [0.8, 0.3, 0],
    }}
    transition={{
      duration: 1.2,
      delay,
      repeat: Infinity,
      repeatDelay: 1.5 + Math.random() * 2,
    }}
  >
    <div
      className="gm-success-splash-ring"
      style={{
        width: 12,
        height: 6,
        borderColor: color,
      }}
    />
  </motion.div>
);

export const SuccessPopupWithConfetti = ({
  marketData,
  onClose,
  onCreateRelated,
}) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const { theme } = useTheme();
  const marketUrl =
    marketData.rainUrl ||
    `https://dev-app.rain.one${ROUTES.DETAIL}?id=${marketData?.poolId}`;
  const arbiscanUrl = marketData.txHash
    ? `https://arbiscan.io/tx/${marketData.txHash}`
    : null;

  const getTimeRemaining = () => {
    if (!marketData.resolutionDate) return "--";
    const now = new Date();
    const end = new Date(marketData.resolutionDate);
    const days = differenceInDays(end, now);
    const hours = differenceInHours(end, now) % 24;
    return `${days}D:${hours.toString().padStart(2, "0")}H`;
  };

  const brandColors = [
    "hsl(68, 100%, 50%)",
    "hsl(68, 100%, 50%)",
    "hsl(259, 72%, 62%)",
    "hsl(200, 100%, 55%)",
    "hsl(144, 100%, 75%)",
  ];

  const raindrops = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 3,
    duration: 0.8 + Math.random() * 0.8,
    size: 2 + Math.random() * 3,
    color: brandColors[Math.floor(Math.random() * brandColors.length)],
  }));

  const splashes = Array.from({ length: 10 }, (_, i) => ({
    id: i,
    x: 5 + Math.random() * 90,
    delay: Math.random() * 2,
    color: brandColors[Math.floor(Math.random() * brandColors.length)],
  }));

  const sortedOutcomes = [...marketData.outcomes].sort(
    (a, b) => b.probability - a.probability,
  );

  return (
    <>
    <motion.div
      className="gm-success-overlay"
    >

      <div className="gm-success-center">
        <motion.div
          initial={{ y: "100%", opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{
            type: "spring",
            damping: 28,
            stiffness: 280,
            delay: 0.15,
          }}
          className="gm-success-card"
        >
          <div className="gm-success-header">
            <div className="gm-success-header-top">
              <div
                className="gm-success-live-badge"
              >
                <span className="gm-success-live-dot-text">Live</span>
              </div>
              <div
                className="gm-success-live-label"
              >
                <div
                  onClick={() => setShowShareModal(true)}
                >
                  <img
                    src="\assets\genrate-market-imgs\share-icon.svg"
                    alt="img"
                    className="img-fluid"
                    style={{ cursor: "pointer" }}
                  />
                </div>
              </div>
            </div>

            <motion.h2
              className="gm-success-question"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
            >
              {marketData.question}
            </motion.h2>
          </div>

          <div className="gm-success-outcomes">
            {sortedOutcomes.slice(0, 4).map((outcome, index) => (
              <motion.div
                key={outcome.id}
                initial={{ opacity: 0, x: -30, scale: 0.9 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                transition={{
                  delay: 0.4 + index * 0.1,
                  type: "spring",
                  stiffness: 300,
                }}
                className="gm-success-outcome"
                style={{
                  backgroundColor: getBackgroundColor(
                    index,
                    theme,
                    sortedOutcomes.length,
                  ),
                }}
              >
                <div className="gm-success-outcome-inner">
                  <span className="gm-success-outcome-label">
                    {outcome.label}
                  </span>
                  <motion.span
                    className="gm-success-outcome-pct"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.6 + index * 0.1, type: "spring" }}
                  >
                    {outcome.probability}%
                  </motion.span>
                </div>
              </motion.div>
            ))}
            {sortedOutcomes.length > 4 && (
              <motion.div
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                className="gm-success-more-outcomes"
                style={{
                  background:
                    "linear-gradient(90deg, rgba(100, 205, 101, 0.10) 0%, rgba(207, 249, 80, 0.10) 34.13%, rgba(219, 164, 60, 0.10) 67.79%, rgba(209, 66, 54, 0.10) 100%)",
                }}
              >
                <span className="gm-success-more-text">
                  +{sortedOutcomes.length - 4} more outcomes
                </span>
              </motion.div>
            )}
          </div>

          <motion.div
            className="gm-success-stats"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.55 }}
          >
            <div>
              <p className="gm-success-stat-label">Volume</p>
              <p className="gm-success-stat-value">
                {getTokenSymbol(
                  marketData.currency === "rain" ? TOKEN_RAIN : TOKEN_USDT,
                )}
                {formatNumbers(+(marketData.liquidity || 0))}
              </p>
            </div>
            <div>
              <p className="gm-success-stat-label">Ends</p>
              <p className="gm-success-stat-value">{getTimeRemaining()}</p>
            </div>
            <div>
              <p className="gm-success-stat-label">Resolver</p>
              <p className="gm-success-stat-value">
                {marketData.resolutionMethod === "ai" ? "AI" : "Manual"}
              </p>
            </div>
          </motion.div>

          <RelatedMarketSuggestions
            category={marketData.category}
            question={marketData.question}
            onSelect={(q) => onCreateRelated && onCreateRelated(q)}
          />

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="gm-success-footer"
          >
            <div className="gm-success-actions">
              <button onClick={onClose} className="gm-success-action-secondary">
                Create Another
              </button>
              <button
                onClick={() => window.open(marketUrl, "_blank")}
                className="gm-success-action-primary"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                >
                  <path
                    d="M15.75 9.75V14.25C15.75 14.6478 15.592 15.0294 15.3107 15.3107C15.0294 15.592 14.6478 15.75 14.25 15.75H3.75C3.35218 15.75 2.97064 15.592 2.68934 15.3107C2.40804 15.0294 2.25 14.6478 2.25 14.25V3.75C2.25 3.35218 2.40804 2.97064 2.68934 2.68934C2.97064 2.40804 3.35218 2.25 3.75 2.25H8.25"
                    stroke="black"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M15.75 2.25L9 9"
                    stroke="black"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M11.25 2.25H15.75V6.75"
                    stroke="black"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                View Market
              </button>
            </div>
            {arbiscanUrl && (
              <button
                onClick={() => window.open(arbiscanUrl, "_blank")}
                className="gm-success-arbiscan"
              >
                View transaction on Arbiscan
              </button>
            )}
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
    <SocialShareModal
      show={showShareModal}
      onHide={() => setShowShareModal(false)}
      poolQuestion={marketData.question}
      poolId={marketData.poolId}
      poolAccessCode={marketData.accessCode || null}
      isPoolPrivate={!marketData.isPublic}
      totalVolume={marketData.liquidity || 0}
      poolOptions={marketData.outcomes?.map((o) => ({
        optionName: o.label,
        percentage: o.probability,
      }))}
      token={marketData.token || null}
    />
    </>
  );
};
